/**
 * Toasts (the last of the peripheral surfaces) — the replacement for `alert()`.
 *
 * Reporting a failed delete, a failed settings write and a failed anything with a modal
 * browser dialog that stopped the page, could not be styled, could not be read by anything
 * looking at the DOM, and had to be dismissed before the player could look at what it was
 * talking about. Everything about that is wrong for a game whose whole surface is a page of
 * text you are reading.
 *
 * **What a toast is for, narrowly**: confirming a write the player asked for, when the result
 * is not already visible on screen. A story vanishing from the shelf needs no toast; a locale
 * saved to the server does. Anything a player must *act on* is not a toast — it is rendered
 * where the action is, which is why a failed turn is `TurnFailure` in the feed and not a
 * corner box that disappears after six seconds.
 *
 * A module-level store rather than context, for `turn/store.ts`'s reason: every surface in
 * the app may raise one, and none of them should have to be wired to a provider to do it.
 */
import { create } from 'zustand';

export type ToastTone = 'ok' | 'error';

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

/** Long enough to read a sentence twice; the host also lets it be dismissed by hand. */
export const TOAST_TIMEOUT_MS = 6_000;

interface ToastStore {
  toasts: Toast[];
  push(tone: ToastTone, message: string): number;
  dismiss(id: number): void;
  clear(): void;
}

let nextId = 0;

export const useToastStore = create<ToastStore>()((set) => ({
  toasts: [],

  push: (tone, message) => {
    const id = (nextId += 1);
    set((state) => ({ toasts: [...state.toasts, { id, tone, message }] }));
    return id;
  },

  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),

  clear: () => set({ toasts: [] }),
}));

/**
 * The two call sites' whole API. Callers are event handlers and mutation callbacks, not
 * components, so this is a function rather than a hook.
 */
export function toastOk(message: string): void {
  useToastStore.getState().push('ok', message);
}

export function toastError(message: string): void {
  useToastStore.getState().push('error', message);
}
